import React, { useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
  AsYouType,
} from 'libphonenumber-js';
import { useAllCountries } from 'modules/Common/utils/Utility';
import 'flag-icons/css/flag-icons.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

// Map of dial code → default ISO2. AsYouType.getCountry() is conservative —
// for shared codes (+1, +7, +44) it stays undefined until enough digits commit
// to one country, and even for unique codes it waits for a valid national
// number prefix. We use this map as a fallback so the flag updates as soon as
// the user types a recognizable +CC.
const DIAL_CODE_DEFAULTS = (() => {
  const map = {};
  for (const iso2 of getCountries()) {
    const cc = getCountryCallingCode(iso2);
    if (!map[cc]) map[cc] = iso2;
  }
  // Overrides for dial codes shared between multiple countries — pick the
  // most-populous as the default flag when no national-number context exists.
  Object.assign(map, {
    1: 'US', // shared with CA + Caribbean nations
    7: 'RU', // shared with KZ
    44: 'GB', // shared with GG/IM/JE
    47: 'NO', // shared with SJ
    39: 'IT', // shared with VA
    212: 'MA', // shared with EH
    358: 'FI', // shared with AX
    590: 'GP', // shared with BL/MF
    599: 'CW', // shared with BQ
    262: 'RE', // shared with YT/TF
  });
  return map;
})();

// Resolve country from an AsYouType instance, falling back to the dial-code
// map when getCountry() is undefined. If the currently-selected country uses
// the same calling code, keep it (preserves an explicit dropdown pick).
const resolveCountryFromAyt = (ayt, currentCountry) => {
  const detected = ayt.getCountry();
  if (detected) return detected;
  const cc = ayt.getCallingCode();
  if (!cc) return null;
  if (currentCountry) {
    try {
      if (getCountryCallingCode(currentCountry) === cc) return currentCountry;
    } catch {}
  }
  return DIAL_CODE_DEFAULTS[cc] || null;
};

// Best-effort country detection from a stored phone string. parsePhoneNumber
// only succeeds on strict, complete numbers — CSV-imported values can be
// formatted (`+7 (495) 123-45-67`) or partial, so we fall back to AsYouType,
// which detects the country from any +-prefixed dial code.
const initFromValue = (val) => {
  if (!val) return { country: 'US', display: '' };
  try {
    const parsed = parsePhoneNumber(val);
    if (parsed?.country) {
      return { country: parsed.country, display: parsed.formatInternational() };
    }
  } catch {}
  if (typeof val === 'string' && val.trim().startsWith('+')) {
    try {
      const ayt = new AsYouType();
      const formatted = ayt.input(val);
      const country = resolveCountryFromAyt(ayt, null);
      if (country) {
        return { country, display: formatted || val };
      }
    } catch {}
  }
  return { country: 'US', display: val };
};

/**
 * Styled phone input that matches the modal field aesthetic.
 *
 * Props:
 *   value      — E.164 string (e.g. "+447911123456") or ""
 *   onChange   — called with updated E.164 string
 *   disabled   — bool
 *   hasError   — bool (red border)
 *   placeholder — string
 */
const PhoneInputField = ({
  value = '',
  onChange,
  disabled = false,
  hasError = false,
  placeholder,
}) => {
  const { t } = useTranslation('common');
  const resolvedPlaceholder =
    placeholder ?? t('common:phoneField.placeholderExample', 'e.g. +1 555 123 4567');
  const allCountries = useAllCountries();

  const countryOptions = useMemo(() => {
    return allCountries
      .filter((c) => c.iso2)
      .flatMap((c) => {
        try {
          const dialCode = getCountryCallingCode(c.iso2.toUpperCase());
          return [{ iso2: c.iso2.toUpperCase(), name: c.name, dialCode: `+${dialCode}` }];
        } catch {
          return [];
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allCountries]);

  const [country, setCountry] = useState(() => initFromValue(value).country);
  const [inputValue, setInputValue] = useState(() => initFromValue(value).display);

  // Track the last value we emitted so we can distinguish external prop changes
  // from our own onChange calls (and avoid a sync loop)
  const lastEmitted = useRef(value);
  // Track the previous value prop to drive the derived-state sync during
  // render. useEffect would do it one paint later — long enough to show the
  // default US flag before the edit-modal data lands.
  const [prevValueProp, setPrevValueProp] = useState(value);

  if (value !== prevValueProp) {
    setPrevValueProp(value);
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      const next = initFromValue(value);
      setCountry(next.country);
      setInputValue(next.display);
    }
  }

  const selectedOption = countryOptions.find((c) => c.iso2 === country);

  // When the user picks a country from the dropdown, switch dial code and keep national digits
  const handleCountrySelect = (e) => {
    const iso2 = e.target.value;
    const opt = countryOptions.find((c) => c.iso2 === iso2);
    if (!opt) return;
    setCountry(iso2);
    setInputValue(opt.dialCode + ' ');
    lastEmitted.current = '';
    onChange('');
  };

  const handleInputChange = (e) => {
    const raw = e.target.value;

    if (!raw) {
      setInputValue('');
      lastEmitted.current = '';
      onChange('');
      return;
    }

    // Strip characters that can't appear in a phone number
    const cleaned = raw.replace(/[^\d\s\-().+]/g, '');

    // Use AsYouType to format and detect country
    // When value starts with + use international mode; otherwise default to current country
    const ayt = new AsYouType(cleaned.startsWith('+') ? undefined : country);
    const formatted = ayt.input(cleaned);
    const detectedCountry = cleaned.startsWith('+')
      ? resolveCountryFromAyt(ayt, country)
      : ayt.getCountry();
    const phoneNum = ayt.getNumber();

    if (detectedCountry) setCountry(detectedCountry);
    setInputValue(formatted || cleaned);
    const emitVal = phoneNum ? phoneNum.format('E.164') : '';
    lastEmitted.current = emitVal;
    onChange(emitVal);
  };

  const containerCls = [
    'flex items-center border rounded-xl bg-white overflow-hidden',
    'focus-within:ring-1 focus-within:ring-primaryColor/40',
    hasError ? 'border-red-400' : 'border-slate-200',
    disabled ? 'bg-slate-50' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerCls}>
      {/* Flag indicator + country dropdown */}
      <div className="relative flex items-center gap-1 pl-3 pr-6 py-2 border-r border-slate-100 shrink-0 select-none">
        {selectedOption ? (
          <span
            className={`fi fi-${selectedOption.iso2.toLowerCase()} shrink-0`}
            style={{ width: 18, height: 14, display: 'inline-block' }}
          />
        ) : (
          <span style={{ width: 18, height: 14, display: 'inline-block' }} />
        )}
        <FontAwesomeIcon
          icon={faChevronDown}
          className="absolute right-2 text-slate-400 pointer-events-none"
          style={{ fontSize: 9 }}
        />
        <select
          value={country}
          onChange={handleCountrySelect}
          disabled={disabled}
          className="absolute inset-0 opacity-0 w-full cursor-pointer disabled:cursor-not-allowed"
          aria-label={t('common:phoneField.dialCodeAria', 'Country dial code')}
        >
          {countryOptions.map((c) => (
            <option
              key={c.iso2}
              value={c.iso2}
            >
              {c.name} ({c.dialCode})
            </option>
          ))}
        </select>
      </div>

      {/* Full editable phone number (includes dial code) */}
      <input
        type="tel"
        value={inputValue}
        onChange={handleInputChange}
        placeholder={resolvedPlaceholder}
        disabled={disabled}
        className="flex-1 min-w-0 text-sm px-3 py-2 bg-transparent focus:outline-none placeholder-slate-400 disabled:text-slate-400"
      />
    </div>
  );
};

export default PhoneInputField;
